import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Loader2, Pill, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	intelligenceApi,
	type IntelligencePeriodPreset,
} from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
	CommandCentreFilters,
	type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import {
	PharmacyMetricCard,
	UnavailablePharmacyCard,
} from "@/components/intelligence/PharmacyMetricCard";
import { PharmacyTrendChart } from "@/components/intelligence/PharmacyTrendChart";
import { PharmacyHospitalComparisonTable } from "@/components/intelligence/PharmacyHospitalComparisonTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatCount, formatInr } from "@/lib/intelligenceFormat";
import { IntelligenceExportButton } from "@/components/intelligence/IntelligenceExportButton";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { buildPharmacyExportRows } from "@/lib/intelligenceExport";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const PRIMARY_METRIC_KEYS = [
	"pharmacy_revenue",
	"pharmacy_items_sold",
	"pharmacy_issue_value",
	"pharmacy_stock_value",
	"pharmacy_expiring_stock",
	"pharmacy_expired_stock",
	"slow_moving_stock",
	"pharmacy_margin",
	"pharmacy_margin_percentage",
];

export default function PharmacyIntelligencePage() {
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isInvestor = user?.role === "INVESTOR";
	const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
	const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
	const chainHospitals = user?.hospitals ?? [];
	const isChainScoped = isChainAdmin || investorHasChainScope;

	const defaultFinancialHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({ preset: "this_month" });
	const [selectedHospitalId, setSelectedHospitalId] = useState("");

	useEffect(() => {
		if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
			setSelectedHospitalId(defaultFinancialHospitalId || "all");
		}
	}, [defaultFinancialHospitalId, selectedHospitalId]);

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-pharmacy"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return superHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (isChainScoped && chainHospitals.length) {
			return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (user?.hospitalId) {
			const name = chainHospitals.find((h) => h.id === user.hospitalId)?.name ?? "My Hospital";
			return [{ id: user.hospitalId, name }];
		}
		return [];
	}, [isSuperAdmin, isChainScoped, superHospitals, chainHospitals, user?.hospitalId]);

	const reportHospitalId = useMemo(
		() =>
			intelligenceReportHospitalId({
				showHospitalFilter,
				selectedHospitalId,
				isChainScoped,
			}),
		[showHospitalFilter, selectedHospitalId, isChainScoped]
	);

	const customReady =
		period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

	const queryParams = {
		period: period.preset as IntelligencePeriodPreset,
		from: period.preset === "custom" ? period.from : undefined,
		to: period.preset === "custom" ? period.to : undefined,
		hospitalId: reportHospitalId,
		compare: true,
	};

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["intelligence-pharmacy", queryParams],
		queryFn: () => intelligenceApi.getPharmacy(queryParams),
		enabled: customReady,
	});

	const primaryMetrics = useMemo(() => {
		if (!data) return [];
		return PRIMARY_METRIC_KEYS.map((key) => data.metrics.find((m) => m.key === key)).filter(
			(m): m is NonNullable<typeof m> => !!m
		);
	}, [data]);

	const basePath = isSuperAdmin ? "/super-admin" : "/hospital-admin";

	return (
		<div className="space-y-6 pb-10">
			<IntelligenceSubNav basePath={basePath} />

			<div>
				<div className="flex items-center gap-2 text-indigo-700">
					<Pill className="h-6 w-6" />
					<h1 className="text-2xl font-bold tracking-tight text-slate-900">
						Pharmacy Intelligence
					</h1>
				</div>
				<p className="text-sm text-muted-foreground mt-1">
					Revenue, consumption, inventory and expiry risk · {data?.period.label ?? "—"}
				</p>
			</div>

			<CommandCentreFilters
				hospitals={hospitalOptions}
				showHospitalFilter={showHospitalFilter}
				hospitalId={selectedHospitalId}
				onHospitalChange={setSelectedHospitalId}
				period={period}
				onPeriodChange={setPeriod}
			/>

			{data && customReady && (
				<div className="flex justify-end gap-2">
					<IntelligenceExportButton
						filename={`pharmacy-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
						buildRows={() => buildPharmacyExportRows(data)}
						label="Quick CSV"
					/>
					<IntelligenceServerExportButton
						report="pharmacy"
						params={queryParams}
						filename={`pharmacy-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
					/>
				</div>
			)}

			{isLoading && customReady && (
				<div className="flex justify-center py-16 text-muted-foreground gap-2">
					<Loader2 className="h-5 w-5 animate-spin" /> Loading…
				</div>
			)}

			{isError && customReady && (
				<Card className="border-destructive/40">
					<CardContent className="pt-4 text-sm text-destructive flex gap-2">
						<AlertTriangle className="h-4 w-4 shrink-0" />
						{(error as Error)?.message ?? "Failed to load pharmacy intelligence"}
					</CardContent>
				</Card>
			)}

			{data && customReady && (
				<>
					{data.statements.length > 0 && (
						<Card>
							<CardContent className="pt-4">
								<ul className="space-y-1 text-sm text-slate-700">
									{data.statements.map((s) => (
										<li key={s}>• {s}</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}

					<div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
						{primaryMetrics.map((m) => (
							<PharmacyMetricCard
								key={m.key}
								metric={m}
								comparisonLabel={data.comparisonPeriod?.label}
							/>
						))}
						{data.unavailable.map((u) => (
							<UnavailablePharmacyCard key={u.key} metric={u} />
						))}
					</div>

					<div className="grid lg:grid-cols-3 gap-4">
						<div className="lg:col-span-2">
							<PharmacyTrendChart dailyTrend={data.dailyTrend} />
						</div>
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-base font-medium">Revenue split</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">OPD (bill lines)</span>
									<span className="font-medium tabular-nums">{formatInr(data.revenueSplit.opd)}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">IPD (discharge PHARMA)</span>
									<span className="font-medium tabular-nums">{formatInr(data.revenueSplit.ipd)}</span>
								</div>
							</CardContent>
						</Card>
					</div>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Expiry risk buckets</CardTitle>
							<p className="text-xs text-muted-foreground">MAIN store · snapshot at period end</p>
						</CardHeader>
						<CardContent className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Bucket</TableHead>
										<TableHead className="text-right">Batches</TableHead>
										<TableHead className="text-right">Qty</TableHead>
										<TableHead className="text-right">Value</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.expiryBuckets.map((b) => (
										<TableRow key={b.key}>
											<TableCell>{b.label}</TableCell>
											<TableCell className="text-right tabular-nums">{b.batchCount}</TableCell>
											<TableCell className="text-right tabular-nums">{formatCount(b.quantity)}</TableCell>
											<TableCell className="text-right tabular-nums">{formatInr(b.value)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Slow-moving stock</CardTitle>
							<p className="text-xs text-muted-foreground">
								No issue/OUT in {data.slowMoving.lookbackDays} days · top batches by value
							</p>
						</CardHeader>
						<CardContent className="overflow-x-auto">
							{data.slowMoving.rows.length === 0 ? (
								<p className="text-sm text-muted-foreground py-4 text-center">No slow-moving batches</p>
							) : (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Medicine</TableHead>
											<TableHead>Batch</TableHead>
											<TableHead className="text-right">Qty</TableHead>
											<TableHead className="text-right">Value</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.slowMoving.rows.map((r) => (
											<TableRow key={r.batchId}>
												<TableCell>{r.drugName}</TableCell>
												<TableCell>{r.batchNo}</TableCell>
												<TableCell className="text-right tabular-nums">{formatCount(r.qtyAsOf)}</TableCell>
												<TableCell className="text-right tabular-nums">{formatInr(r.value)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Top medicines</CardTitle>
							<p className="text-xs text-muted-foreground">By issue/MRP value in period</p>
						</CardHeader>
						<CardContent className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Medicine</TableHead>
										<TableHead className="text-right">Qty</TableHead>
										<TableHead className="text-right">Issue value</TableHead>
										<TableHead className="text-right">Margin</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.topDrugs.map((d) => (
										<TableRow key={d.drugId}>
											<TableCell>{d.medicineName}</TableCell>
											<TableCell className="text-right tabular-nums">{formatCount(d.totalQty)}</TableCell>
											<TableCell className="text-right tabular-nums">{formatInr(d.mrpValue)}</TableCell>
											<TableCell className="text-right tabular-nums">{formatInr(d.margin)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					{data.hospitalComparison.enabled && (
						<PharmacyHospitalComparisonTable rows={data.hospitalComparison.rows} />
					)}

					<Card className="border-amber-200/60 bg-amber-50/30">
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-medium">Data limitations</CardTitle>
						</CardHeader>
						<CardContent>
							<ul className="space-y-1 text-xs text-muted-foreground">
								{data.limitations.map((l) => (
									<li key={l}>• {l}</li>
								))}
							</ul>
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}
